/* @flow */
import Button from 'components/Button.jsx';
import ChatSettingsActions from 'settings/actions/ChatSettingsActions';
import CheckboxList from 'components/CheckboxList.jsx';
import { Container } from 'flux/utils';
import CurrentUser from 'auth/CurrentUser';
import ChatSettingsStore from 'settings/stores/ChatSettingsStore';
import DateConstants from 'constants/DateConstants';
import Fieldset from 'components/Fieldset.jsx';
import Legend from 'components/Legend.jsx';
import OfficeHours from 'entities/OfficeHours';
import OfficeHoursDay from 'entities/OfficeHoursDay';
import React, { Component } from 'react';
import TimePickerInput from 'components/TimePickerInput.jsx';
import Text from 'components/Text.jsx';
import ToastActions from 'actions/ToastActions';
import Label from 'components/Label.jsx';

import cx from 'classnames';

import './styles/OfficeHoursPicker.scss';

const OFFICE_HOUR_DAYS = new Map();
const OFFICE_HOUR_DAYS_COMPACT = new Map();
[0, 1, 2, 3, 4, 5, 6].forEach(index => {
  OFFICE_HOUR_DAYS.set(index, DateConstants.WEEKDAYS[index]);
  OFFICE_HOUR_DAYS_COMPACT.set(index, DateConstants.WEEKDAYS_COMPACT[index]);
});

type OfficeHourTimes = {
  startTime: ?Date,
  endTime: ?Date
};

type Props = {
  showSubmitButton: ?boolean,
  showToggleButton: ?boolean,
  showToastMessages: ?boolean,
  showIntroText: ?boolean,
  onSubmit: ?Function,
  compact: ?boolean,
};

type State = {
  officeHours: ?OfficeHours,
  startTime: ?Date,
  endTime: ?Date,
  twoWayMessaging: ?boolean,
  availabilityChanges: OfficeHourTimes,
};

class OfficeHoursPicker extends Component {

  props: Props;

  state: State = {
    officeHours: null,
    startTime: null,
    endTime: null,
    twoWayMessaging: null,
    availabilityChanges: {
      startTime: null,
      endTime: null,
    },
  };

  static getStores(): Array<Object> {
    return [ChatSettingsStore, CurrentUser];
  }

  static calculateState(prevState): State {
    const officeHours = ChatSettingsStore.getOfficeHours();
    let startTime;
    let endTime;
    let twoWayMessaging;
    if (officeHours) {
      const availability = officeHours.getAvailability();
      startTime = officeHours.hasAvailability() ?
                    OfficeHoursPicker.getDateObject(availability[0].getStartTime()) : null;
      endTime = officeHours.hasAvailability() ?
                  OfficeHoursPicker.getDateObject(availability[0].getEndTime()) : null;
      twoWayMessaging = CurrentUser.getUser().hasTwoWayMessaging();
    }

    return {
      officeHours,
      startTime,
      endTime,
      twoWayMessaging,
      availabilityChanges: (prevState && prevState.availabilityChanges) || {
        startTime: null,
        endTime: null,
      },
    };
  }

  static getDateObject(seconds: number): Date {
    // start and end time from API are stored as seconds
    // DateTimePicker expects Date object
    // this converts API seconds to Date object for the DateTimePicker
    return new Date(2015, 0, 0, 0, 0, seconds);
  }

  getSecondsFromTime(time: ?Date): number {
    if (!time) {
      return 0;
    }
    // this converts Date object from DateTimePicker into seconds for API

    const SECONDS_IN_HOUR = 60 * 60;
    const SECONDS_IN_MIN = 60;

    const date = new Date(time);
    return date.getHours() * SECONDS_IN_HOUR + date.getMinutes() * SECONDS_IN_MIN;
  }

  commitAvailabilityChanges(newTimes: OfficeHourTimes) {
    const { showToastMessages } = this.props;
    const days = this.refs.days.getValue();
    const newStartTime = newTimes.startTime ? this.getSecondsFromTime(newTimes.startTime)
                                          : this.getSecondsFromTime(this.state.startTime);
    const newEndTime = newTimes.endTime ? this.getSecondsFromTime(newTimes.endTime)
                                      : this.getSecondsFromTime(this.state.endTime);
    const available = days.map(day => OfficeHoursDay.create(day, newStartTime, newEndTime));

    ChatSettingsActions
      .setOfficeHours(available)
      .then(() => showToastMessages && ToastActions.showSuccessToast('Office hours updated'))
      .catch(showToastMessages ? ToastActions.showDefaultErrorToast : () => {});

    if (this.props.showSubmitButton && this.props.onSubmit) {
      this.props.onSubmit();
    }
  }

  handleAvailabilityChange(newTimes: OfficeHourTimes) {
    if (this.props.showSubmitButton) {
      if (newTimes.startTime) {
        this.setState({ startTime: newTimes.startTime });
      }
      if (newTimes.endTime) {
        this.setState({ endTime: newTimes.endTime });
      }
      this.setState({
        availabilityChanges: Object.assign(this.state.availabilityChanges, newTimes),
      });
    } else {
      this.commitAvailabilityChanges(newTimes);
    }
  }

  handleEndChange(newEndTime: ?Date) {
    if (newEndTime) {
      this.handleAvailabilityChange({ startTime: null, endTime: newEndTime });
    }
  }

  handleStartChange(newStartTime: ?Date) {
    if (newStartTime) {
      this.handleAvailabilityChange({ startTime: newStartTime, endTime: null });
    }
  }

  toggleOfficeHours() {
    const { showToastMessages } = this.props;
    const { officeHours } = this.state;

    if (officeHours && officeHours.hasAvailability()) {
      ChatSettingsActions
        .disableOfficeHours()
        .then(() =>
          showToastMessages && ToastActions.showSuccessToast('Office hours have been canceled'))
        .catch(showToastMessages ? ToastActions.showDefaultErrorToast : () => {});
    } else {
      ChatSettingsActions
        .enableOfficeHours()
        .then(() =>
          showToastMessages && ToastActions.showSuccessToast('Office hours have been set'))
        .catch(showToastMessages ? ToastActions.showDefaultErrorToast : () => {});
    }
  }

  renderOptions() {
    const officeHours = this.state.officeHours;
    if (!officeHours) {
      return null;
    }
    const availability = officeHours.getAvailability();

    const times = (
      <div>
        <TimePickerInput
          ref="startTime"
          value={this.state.startTime}
          max={this.state.endTime}
          onChange={(newStartTime) => this.handleStartChange(newStartTime)}
        />
        <Text color="thunder" size="large">
          {' - '}
        </Text>
        <TimePickerInput
          ref="endTime"
          value={this.state.endTime}
          min={this.state.startTime}
          onChange={(newEndTime) => this.handleEndChange(newEndTime)}
        />
      </div>
    );

    const days = (
      <CheckboxList
        ref="days"
        className="OfficeHoursPicker-officeHoursDays"
        options={this.props.compact ? OFFICE_HOUR_DAYS_COMPACT : OFFICE_HOUR_DAYS}
        defaultChecked={availability.map(day => day.getDay())}
        onChange={(newTimes) => this.handleAvailabilityChange(newTimes)}
        direction={this.props.compact ? 'horizontal' : 'vertical'}
      />
    );

    return (
      <div className={cx({ 'OfficeHoursPicker-compactDateTime': this.props.compact })}>
        <div className="OfficeHoursPicker-timePicker">
          <Label>Time</Label>
          {times}
        </div>
        <div className="OfficeHoursPicker-datePicker">
          <Label>Day(s)</Label>
          {days}
        </div>
      </div>
    );
  }


  render() {
    let options;
    let toggleButton;

    const { officeHours, twoWayMessaging } = this.state;
    const officeHoursAreOn = twoWayMessaging && officeHours && officeHours.hasAvailability();
    if (officeHoursAreOn && officeHours) {
      options = this.renderOptions();

      toggleButton = (
        <Button
          color="thunder"
          level="text"
          size="xsmall"
          key="enabled"
          onClick={() => this.toggleOfficeHours()}>
          Turn off office hours
        </Button>
      );
    } else {
      toggleButton = (
        <Button
          key="disabled"
          level="secondary"
          size="xsmall"
          disabled={!twoWayMessaging}
          onClick={() => this.toggleOfficeHours()}>
          Turn on office hours
        </Button>
      );
    }

    const submitButton = (
      <Button
        className="OfficeHoursPicker-submit"
        level="primary"
        size="xsmall"
        onClick={() => this.commitAvailabilityChanges(this.state.availabilityChanges)}
        eventElementName="OfficeHoursPicker-submit">
        Set office hours
      </Button>
    );

    const introText = (
      <div>
        Office hours
        <p>
         Let people know the best times to reach you.
        </p>
      </div>
    );

    return (
      <Fieldset>
        <Legend>
          {this.props.showIntroText && introText}
          {!twoWayMessaging && (
            <p>
              To enable Office hours, set Message options to 2-way conversations.
            </p>
          )}
        </Legend>
        {options}
        {this.props.showToggleButton && toggleButton}
        {this.props.showSubmitButton && officeHoursAreOn && submitButton}
      </Fieldset>
    );
  }
}

export default Container.create(OfficeHoursPicker);




// WEBPACK FOOTER //
// ./src/settings/components/OfficeHoursPicker.jsx